/**
 * display-prefs — tiny external store for Celestia display accessibility.
 *
 * Holds the active locale, dyslexia-font toggle, and text-scale step. The
 * scale + font apply via `data-*` attributes on the game-screen root, read by
 * styles/celestia-a11y.css custom properties — no per-component styling.
 *
 * Deliberately minimal (no generic settings framework, per Simplicity First):
 * three readable values + setters + a useSyncExternalStore-compatible
 * subscribe/getSnapshot.
 */

import type { LocaleCode } from "../i18n";

export type TextScale = "normal" | "large" | "xlarge";

export interface DisplayPrefs {
  readonly locale: LocaleCode;
  readonly dyslexiaFont: boolean;
  readonly textScale: TextScale;
}

export interface DisplayPrefsStore {
  readonly subscribe: (cb: () => void) => () => void;
  readonly getSnapshot: () => DisplayPrefs;
  readonly setLocale: (locale: LocaleCode) => void;
  readonly toggleDyslexiaFont: () => void;
  readonly setTextScale: (scale: TextScale) => void;
}

export function createDisplayPrefsStore(): DisplayPrefsStore {
  let state: DisplayPrefs = {
    locale: "ko-KR",
    dyslexiaFont: false,
    textScale: "normal",
  };
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const cb of listeners) cb();
  };

  const set = (next: DisplayPrefs): void => {
    state = next;
    emit();
  };

  return {
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    getSnapshot: () => state,
    setLocale: (locale) => set({ ...state, locale }),
    toggleDyslexiaFont: () =>
      set({ ...state, dyslexiaFont: !state.dyslexiaFont }),
    setTextScale: (textScale) => set({ ...state, textScale }),
  };
}
